import React from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';

export interface TicketCardProps {
  ticketId: string;
  state?: string;
  title?: string;
  ticketLink?: string;
  createdOn?: string;
  source?: string;
  actionLabel?: string;
  onAction?: (ticketId: string) => void;
}

const BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  SNOW: { bg: '#dbeafe', fg: '#1d4ed8' },
  UFN: { bg: '#fef3c7', fg: '#92400e' },
  COSMIC: { bg: '#ede9fe', fg: '#6b21a8' },
};

const useStyles = makeStyles({
  container: {
    background: '#f7f7f7',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  ticketIdText: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  badge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: 500,
  },
  actionLink: {
    color: '#ff4d00',
    fontWeight: 600,
    fontSize: '13px',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { textDecoration: 'underline', outline: '2px solid #ff4d00', outlineOffset: '2px' },
  },
  details: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.8',
  },
  label: {
    display: 'inline-block',
    width: '90px',
    color: '#666',
  },
  stateValue: {
    color: '#107c10',
    fontWeight: 500,
  },
  ticketLink: {
    color: '#0d6efd',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
});

/**
 * Reusable ticket card component — consistent look & feel across
 * escalate list, open tickets, ticket search results, etc.
 */
const TicketCard: React.FC<TicketCardProps> = React.memo(({
  ticketId,
  state = 'Active',
  title = '--',
  ticketLink,
  createdOn,
  source = 'SNOW',
  actionLabel,
  onAction,
}) => {
  const classes = useStyles();
  const badgeColor = BADGE_COLORS[source] || BADGE_COLORS.SNOW;

  const handleAction = React.useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    onAction?.(ticketId);
  }, [onAction, ticketId]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') handleAction(e);
  }, [handleAction]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.ticketIdText}>{ticketId}</span>
          <span className={classes.badge} style={{ background: badgeColor.bg, color: badgeColor.fg }}>
            {source}
          </span>
        </div>
        {actionLabel && onAction && (
          <a
            href="#"
            className={classes.actionLink}
            onClick={handleAction}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`${actionLabel} ticket ${ticketId}`}
          >
            {actionLabel}
          </a>
        )}
      </div>
      <div className={classes.details}>
        <div>
          <span className={classes.label}>Ticket ID:</span>
          {ticketLink ? (
            <a href={ticketLink} target="_blank" rel="noopener noreferrer" className={classes.ticketLink}>
              {ticketId}
            </a>
          ) : (
            <span>{ticketId}</span>
          )}
        </div>
        <div>
          <span className={classes.label}>State:</span>
          <span className={classes.stateValue}>{state}</span>
        </div>
        <div>
          <span className={classes.label}>Title:</span>
          <span>{title}</span>
        </div>
        {createdOn && (
          <div>
            <span className={classes.label}>Created On:</span>
            <span>{createdOn}</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default TicketCard;
